'use client';

import React from 'react';
import {
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { useAnalyticsContext } from '../analytics-context';

export function ChartDownloads() {
  const { downloads, selectedFilter, payload } = useAnalyticsContext();

  const chartData =
    downloads.data?.data?.map(
      (item: { label: string; downloads: number }) => ({
        label: item.label,
        downloads: item.downloads,
      }),
    ) ?? [];

  const isAwaitingRange =
    selectedFilter === 'custom' && payload === null;

  return (
    <Card className="border-none">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            File Downloads
            <span className="text-sm text-primary">
              ({downloads.data?.totalDownloads ?? 0})
            </span>
          </CardTitle>
          <CardDescription className="flex items-center gap-2 mt-1">
            <span className="text-muted-foreground">Filter:</span>
            <Badge variant="outline" className="capitalize">
              {selectedFilter === 'custom'
                ? 'Custom range'
                : selectedFilter.toLowerCase()}
            </Badge>
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="pl-2">
        {downloads.isLoading ? (
          <div className="h-[300px] flex items-center justify-center">
            <div className="h-56 w-full animate-pulse rounded-xl bg-muted/60" />
          </div>
        ) : isAwaitingRange ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Select a start and end date to view custom data.
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No data available for the selected period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="downloadsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="4 4"
                className="stroke-muted/50"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={true}
                tickMargin={8}
                className="text-muted-foreground text-xs"
                interval={0} // show all ticks; consider changing to 'preserveStartEnd' if overcrowded
                tickFormatter={(value: string) => {
                  const parts = value.split(' - ');
                  return window.innerWidth < 640 ? parts[0] : value; // Responsive: only show start date on small screens
                }}
              />

              <YAxis
                tickMargin={8}
                axisLine={true}
                tickLine={false}
                allowDecimals={false}
                className="text-muted-foreground text-xs"
                tickFormatter={value => value.toLocaleString()}
              />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{
                  borderRadius: 8,
                  fontSize: 12,
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
                formatter={(value: number) => [
                  `${value.toLocaleString()}`,
                  'Downloads',
                ]}
                labelFormatter={label => label}
              />
              <Area
                type="monotone"
                dataKey="downloads"
                stroke="none"
                fill="url(#downloadsGradient)"
              />
              <Line
                type="monotone"
                dataKey="downloads"
                stroke="var(--primary)"
                strokeWidth={2}
                dot={{
                  r: 4,
                  fill: 'var(--primary)',
                  strokeWidth: 2,
                  stroke: 'var(--primary)',
                }}
                activeDot={{
                  r: 6,
                  fill: 'var(--primary)',
                  strokeWidth: 2,
                  stroke: 'var(--primary)',
                }}
                name="Downloads"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
